import { useCallback, useEffect, useMemo, useState } from 'react';
import { DefaultRootState, useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory, useParams } from 'react-router';

import {
  DerivativesOpenedPositionProjection,
  DerivativesOrderProjection,
} from 'common/api/autogenerated/trading-gateway';

import { useI18n } from 'common/hooks/useI18n/useI18n.hook';
import { usePopup } from 'common/hooks/usePopup';
import { CloseDerivativePositionPopup, FuturePositionAddTPSLPopup } from 'common/popups';
import { FuturePositionAddTPSLPopupForm } from 'common/popups/FuturePositionAddTPSLPopup/FuturePositionAddTPSL.component';

import { createOrderForPartialPositionClose } from 'views/derivatives/dashboard/components/OrderForm/helpers';
import { actionGetDerivativesOrders, actionGetDerivativesPositions } from 'views/derivatives/redux/derivatives.actions';
import { deleteDerivativeOrder, postDerivativeOrder } from 'views/derivatives/redux/derivatives.api';

import { routes } from 'routes/routes.constants';

export const useDerivativesPositions = (
  filterFn?: (order: DerivativesOpenedPositionProjection) => boolean,
  waitFn = false,
  useCached = false,
) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { show, hide } = usePopup();
  const { translate } = useI18n();
  const [initiallyWasLoaded, setInitiallyWasLoaded] = useState(false);

  const params = useParams<{ symbol?: string; id?: string }>();

  const { isAuthenticated } = useSelector((state: DefaultRootState) => state.common.user);

  const loadingReducer = useSelector((state: DefaultRootState) => state.derivativesReducer.loading.positionsInitial);
  const loading = useMemo(() => (isAuthenticated ? loadingReducer : false), [isAuthenticated, loadingReducer]);

  const positions = (useSelector((state: DefaultRootState) => state.derivativesReducer.positions) || []).filter(
    (position: DerivativesOpenedPositionProjection) => {
      if (!filterFn) return true;
      return filterFn(position);
    },
  );

  const onNavigatePositionDetails = useCallback(
    (id: DerivativesOpenedPositionProjection['id'], symbol: string) => {
      history.push(generatePath(routes.getDerivativesEditPosition(), { symbol, id }));
    },
    [history],
  );

  const onViewPositionOnGrid = useCallback(
    (id: DerivativesOpenedPositionProjection['id'], symbol: string) => {
      const pairName = params.symbol || symbol;

      if (Number(id) === Number(params.id))
        return history.push(generatePath(routes.getDerivativesAssetPage(), { symbol: pairName }));

      return history.push(
        generatePath(routes.getDerivativesEditPositionFromGrid(), {
          symbol: pairName,
          id,
        }),
      );
    },
    [history, params.id, params.symbol],
  );

  const onViewPositionOnDashboard = useCallback(
    (id: DerivativesOpenedPositionProjection['id'], symbol: string) => {
      const pairName = params.symbol || symbol;
      if (Number(id) === Number(params.id))
        return history.push(generatePath(routes.getDerivativesAssetPage(), { symbol: pairName }));

      return history.push(
        generatePath(routes.getDerivativesEditPosition(), {
          symbol: pairName,
          id,
        }),
      );
    },
    [history, params.id, params.symbol],
  );

  const onClosePartially = useCallback(
    (id: DerivativesOpenedPositionProjection['id']) =>
      show(
        CloseDerivativePositionPopup({
          id,
          title: translate.toText('mobile:derivative:position:popup:close-partially:title'),
        }),
      ),
    [show, translate],
  );

  const onCloseCompletely = useCallback(
    (id: DerivativesOpenedPositionProjection['id']): Promise<void> => {
      const position = positions.find((i) => i.id === id);
      // TODO: Fix this the next time the file is edited.

      if (!position) return new Promise((resolve) => resolve());

      const order = createOrderForPartialPositionClose(position, Number(position.contracts));
      return postDerivativeOrder(order).then(() => hide());
    },
    [hide, positions],
  );

  const onAddTakeProfitStopLoss = useCallback(
    (position: DerivativesOpenedPositionProjection) => show(FuturePositionAddTPSLPopup({ position })),
    [show],
  );

  const onEditTakeProfitStopLoss = useCallback(
    (position: DerivativesOpenedPositionProjection, update?: Partial<FuturePositionAddTPSLPopupForm>) =>
      show(FuturePositionAddTPSLPopup({ position, update })),
    [show],
  );

  const onDeleteTakeProfitStopLoss = useCallback(
    // 85523 TEMP CODE
    (order: DerivativesOrderProjection) => deleteDerivativeOrder(order?.id),
    // 85523 OLD CODE
    // (order: DerivativesOrderProjection) => deleteDerivativeOrder(order?.id),
    [],
  );

  const reload = useCallback(() => dispatch(actionGetDerivativesOrders()), [dispatch]);

  const userNotAuthenticated = useMemo(() => !isAuthenticated, [isAuthenticated]);
  const isWaitingToProvideFilterFn = useMemo(() => waitFn && !filterFn, [filterFn, waitFn]);
  const existsCached = useMemo(() => useCached && !!positions?.length, [positions?.length, useCached]);

  useEffect(() => {
    if (userNotAuthenticated || isWaitingToProvideFilterFn || initiallyWasLoaded || existsCached) return;
    dispatch(actionGetDerivativesPositions());
    setInitiallyWasLoaded(true);
  }, [dispatch, existsCached, initiallyWasLoaded, isWaitingToProvideFilterFn, userNotAuthenticated]);

  return {
    positions,
    loading,
    reload,
    onCloseCompletely,
    onViewPositionOnGrid,
    onViewPositionOnDashboard,
    onNavigatePositionDetails,
    onClosePartially,
    onAddTakeProfitStopLoss,
    onEditTakeProfitStopLoss,
    onDeleteTakeProfitStopLoss,
  };
};
